// The pages of a scan, one at a time: the page's name and address, a way to every problem of the analysis, the
// summary of its problems, its screenshot with the failing elements marked and its problems; the page's
// measurements stay folded under «Detalles técnicos». And the website's "Pages" tab that shows them.
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  ClockIcon,
  ExternalLinkIcon,
  FileIcon,
  GlobeIcon,
  LightningBoltIcon,
} from '@radix-ui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, Modal, ModalOverlay } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  type Device,
  meQuery,
  type PageIssue,
  pageQuery,
  type ScanDetail,
  type ScannedPage,
  scanQuery,
  sitePagesQuery,
  siteQuery,
} from '../api';
import { deviceFor, elementsOf, findElement } from '../element-logic';
import { usePersisted } from '../persist';
import {
  groupProblems,
  isDelegatedOnly,
  isFullyDiscarded,
  type Problem,
  splitCategories,
} from '../results-logic';
import { Button, buttonClass, Message, useArrivalFocus, usePageTitle, WebsiteLink } from '../ui';
import { formatBytes, formatSeconds, pathOf, publicPage } from '../util';
import { ElementNavigator } from './element-guide';
import { rightsOf } from './follow-state';
import { Inspector } from './inspector';
import { PagesList } from './pages-list';
import { CountingTerms, PageSummary, ResultContext, ResultsTabs, type TabKey } from './results';

/**
 * The website's "Pages" tab. Without a page chosen, the list of every page ever analysed (one card each, with its
 * latest result); with one, that page alone: back to the list, the page, and a way to every problem of the analysis.
 * The tab bar already says where we are, so there is no heading of its own.
 */
export function PagesTab() {
  const { siteId } = useParams({ from: '/sites/$siteId' });
  const search = useSearch({ from: '/sites/$siteId/paginas' });
  return (
    <div className="pages-tab">
      {search.p ? <PageScreen siteId={siteId} search={search} /> : <PagesList siteId={siteId} />}
    </div>
  );
}

/** One page of the website: the analysis that holds its latest result (or the one the address names). */
function PageScreen({
  siteId,
  search,
}: {
  siteId: string;
  search: { scan?: string; p?: string; disp?: 'movil'; e?: string; k?: string };
}) {
  const { t } = useTranslation();
  const site = useQuery(siteQuery(siteId));
  const pages = useQuery(sitePagesQuery(siteId));
  const entry = pages.data?.find((page) => page.key === search.p);
  const scanId = search.scan ?? entry?.scanId;
  const scan = useQuery({ ...scanQuery(scanId ?? ''), enabled: Boolean(scanId), retry: false });
  // With a page to show, its heading takes the focus (see PageDetail); the message does it when there is none.
  const showsPage = scan.data?.status === 'done';
  const message = useArrivalFocus<HTMLParagraphElement>(
    !showsPage && !pages.isPending && !(scanId && scan.isPending),
  );
  usePageTitle(t('site.tabs.pages'), site.data?.name);
  const back = (
    <p className="page-back">
      <Link to="/sites/$siteId/paginas" params={{ siteId }} search={{}} className="back-link">
        {t('results.backToPages')}
      </Link>
    </p>
  );

  if (pages.isPending || (scanId && scan.isPending)) return <p role="status">{t('app.loading')}</p>;
  if (pages.isError || scan.isError) {
    const missing = scan.error instanceof ApiError && scan.error.status === 404;
    return (
      <>
        {back}
        <Message tone="error">{missing ? t('notFound.text') : t('errors.generic')}</Message>
      </>
    );
  }
  if (scan.data?.status !== 'done' || !scan.data.pages.some((page) => page.key === search.p)) {
    return (
      <>
        {back}
        <p ref={message} tabIndex={-1} className="focus:outline-none">
          {scan.data && scan.data.status !== 'done'
            ? t(scan.data.outcome === 'failed' ? 'scan.pages.failed' : 'scan.pages.notReady')
            : t('pagesList.notFound')}
        </p>
      </>
    );
  }
  return (
    <PagesView
      scan={scan.data}
      siteId={siteId}
      selected={search.p ?? ''}
      device={search.disp ?? 'escritorio'}
      elementId={search.e}
      problemKey={search.k}
    />
  );
}

/** Narrower than this, the capture goes under the problems and folds away until a problem is chosen. */
const STACKED_BELOW = 740;

/** Whether the element is narrower than `limit`: the layout the container decides, not the screen. */
function useNarrow(ref: RefObject<HTMLElement | null>, limit: number) {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () => setNarrow(element.clientWidth <= limit);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, limit]);
  return narrow;
}

export function PagesView({
  scan,
  siteId,
  selected,
  device,
  elementId,
  problemKey,
}: {
  /** The analysis that holds the latest result of the page chosen (or the one the address names). */
  scan: ScanDetail;
  siteId: string;
  selected: string;
  device: Device;
  elementId?: string;
  problemKey?: string;
}) {
  const { t } = useTranslation();
  const current = scan.pages.find((page) => page.key === selected);
  if (!current) return <p>{t('scan.pages.none')}</p>;
  return (
    <div className="pages-view">
      {/* On a line of its own right under the tabs, before the content. */}
      <p className="page-back">
        <Link to="/sites/$siteId/paginas" params={{ siteId }} search={{}} className="back-link">
          {t('results.backToPages')}
        </Link>
      </p>
      <div className="page-main">
        <PageDetail
          scan={scan}
          siteId={siteId}
          page={current}
          device={device}
          elementId={elementId}
          problemParam={problemKey}
        />
      </div>
    </div>
  );
}

function Facts({ page }: { page: ScannedPage }) {
  const { t, i18n } = useTranslation();
  const facts: { key: string; icon: typeof ClockIcon; label: string; value: string }[] = [];
  if (page.status !== null)
    facts.push({
      key: 'status',
      icon: GlobeIcon,
      label: t('scan.facts.status'),
      value: `HTTP ${page.status}`,
    });
  if (page.timings.loadMs !== null)
    facts.push({
      key: 'load',
      icon: ClockIcon,
      label: t('scan.facts.load'),
      value: formatSeconds(page.timings.loadMs, i18n.language),
    });
  if (page.timings.ttfbMs !== null)
    facts.push({
      key: 'ttfb',
      icon: LightningBoltIcon,
      label: t('scan.facts.ttfb'),
      value: formatSeconds(page.timings.ttfbMs, i18n.language),
    });
  if (page.bytes)
    facts.push({
      key: 'bytes',
      icon: FileIcon,
      label: t('scan.facts.bytes'),
      value: formatBytes(page.bytes, i18n.language),
    });
  if (page.requests)
    facts.push({
      key: 'requests',
      icon: FileIcon,
      label: t('scan.facts.requests'),
      value: String(page.requests),
    });
  if (page.lang) facts.push({ key: 'lang', icon: GlobeIcon, label: t('scan.facts.lang'), value: page.lang });
  const features = page.features
    ? (['forms', 'video', 'audio', 'iframes'] as const).filter((feature) => page.features?.[feature])
    : [];
  return (
    <>
      <dl className="page-facts">
        {facts.map((fact) => (
          <div key={fact.key}>
            <dt>
              <fact.icon aria-hidden="true" />
              {fact.label}
            </dt>
            <dd>{fact.value}</dd>
          </div>
        ))}
      </dl>
      {features.length > 0 && (
        <p className="text-sm text-ink-muted">
          {t('scan.facts.features', {
            list: features.map((feature) => t(`scan.features.${feature}`)).join(', '),
          })}
        </p>
      )}
    </>
  );
}

function PageDetail({
  scan,
  siteId,
  page,
  device,
  elementId,
  problemParam,
}: {
  scan: ScanDetail;
  siteId: string;
  page: ScannedPage;
  device: Device;
  /** The element opened (the id of one of its occurrences), from the address. */
  elementId: string | undefined;
  /** The problem opened (its key), from the address; when there is none, the one of the element opened. */
  problemParam: string | undefined;
}) {
  const { t } = useTranslation();
  const { data: me } = useQuery(meQuery);
  const rights = rightsOf(me);
  const detail = useQuery(pageQuery(scan.id, page.key));
  const navigate = useNavigate({ from: '/sites/$siteId/paginas' });
  const heading = useArrivalFocus();
  const [heights, setHeights] = useState<Partial<Record<Device, number>>>({});

  // A new page from the list, the map or the arrows: its heading takes the focus (not on the first
  // render, and not twice for the same page).
  const shown = useRef(page.key);
  useEffect(() => {
    if (shown.current === page.key) return;
    shown.current = page.key;
    setHeights({});
    heading.current?.focus();
  }, [page.key, heading]);

  const issues = useMemo(() => detail.data?.issues ?? [], [detail.data]);
  const allProblems = useMemo(() => groupProblems(issues), [issues]);
  const problems = useMemo(
    () =>
      groupProblems(splitCategories(issues).problems).filter(
        (problem) => !isFullyDiscarded(problem) && !isDelegatedOnly(problem),
      ),
    [issues],
  );
  const [tab, setTab] = usePersisted<TabKey>(`tab:${scan.id}:${page.key}`, 'problems');
  const selection = useMemo(() => findElement(allProblems, elementId), [allProblems, elementId]);

  // One place decides what is selected and on which device: the capture, the list, the diagnosis and the
  // solution all read it, so they cannot disagree. An element found on one device only opens on that one.
  const select = (id: string | null) => {
    const found = findElement(allProblems, id ?? undefined);
    const next: Device = found ? deviceFor(found.element, device) : device;
    navigate({
      search: (previous) => ({
        ...previous,
        e: id ?? undefined,
        disp: next === 'movil' ? 'movil' : undefined,
      }),
      replace: true,
      // Choosing an element changes what is shown here, not where the person is on the page.
      resetScroll: false,
    });
  };
  // The problem whose panel is open: the one in the address, or the one the element in the address is of.
  const openKey = problemParam ?? selection?.problem.key;
  // Opening a problem opens its first element on the capture; null goes back to the list of problems.
  const openProblem = (problem: Problem<PageIssue> | null) => {
    const first = problem ? elementsOf(problem)[0] : undefined;
    const next: Device = first ? deviceFor(first, device) : device;
    navigate({
      search: (previous) => ({
        ...previous,
        k: problem?.key,
        e: first?.id,
        disp: next === 'movil' ? 'movil' : undefined,
      }),
      replace: true,
      // Opening or closing a problem keeps the view where it is; the panel takes the focus to its name.
      resetScroll: false,
    });
  };
  const setDevice = (next: Device) =>
    navigate({
      search: (previous) => ({ ...previous, disp: next === 'movil' ? 'movil' : undefined }),
      replace: true,
      // Switching device changes the capture, not the place on the page.
      resetScroll: false,
    });

  // Under the width where capture and problems do not fit side by side, the problems come first and the
  // capture folds under them; choosing a problem opens it.
  const layout = useRef<HTMLDivElement>(null);
  const stacked = useNarrow(layout, STACKED_BELOW);
  const [captureOpen, setCaptureOpen] = useState(false);
  // On a narrow screen the capture of the problem that is open is a full view of its own, with the way
  // back to the solution and the move between its elements.
  const [captureShown, setCaptureShown] = useState(false);
  // Closing that view (a modal) gives the focus back to the button that opened it, so a keyboard continues
  // from there instead of from the top of the page.
  const captureWasShown = useRef(false);
  useEffect(() => {
    if (captureShown) {
      captureWasShown.current = true;
      return;
    }
    if (!captureWasShown.current) return;
    captureWasShown.current = false;
    document.getElementById('ver-captura')?.focus({ preventScroll: true });
  }, [captureShown]);
  const inspector = (
    <Inspector
      key={page.key}
      scan={scan}
      page={page}
      device={device}
      onDevice={setDevice}
      selected={selection}
      onSelect={(id) => select(id)}
      onHeight={(on, height) => setHeights((before) => ({ ...before, [on]: height }))}
    />
  );
  const panelOpen = Boolean(openKey);

  return (
    <article className="page-detail" aria-labelledby="pagina-titulo">
      <header className="page-detail-head">
        <div className="page-detail-title">
          <h2 id="pagina-titulo" ref={heading} tabIndex={-1}>
            {page.title || pathOf(page.finalUrl)}
          </h2>
          <a href={page.finalUrl} className="page-url" target="_blank" rel="noreferrer noopener">
            {page.finalUrl}
            <ExternalLinkIcon aria-hidden="true" />
            <span className="sr-only">{t('scan.pages.newTab')}</span>
          </a>
        </div>
        <Link
          to="/sites/$siteId/scans/$scanId"
          params={{ siteId, scanId: scan.id }}
          className={buttonClass('secondary')}
        >
          {t('scans.seeAllProblems')}
          <ArrowRightIcon aria-hidden="true" />
        </Link>
      </header>
      {page.blocked || page.error ? (
        <Message tone="error">
          {page.blocked
            ? t('scan.pageBlocked', { reason: page.blockedReason ?? `HTTP ${page.status}` })
            : t('scan.pageError', { error: page.error })}
        </Message>
      ) : (
        <>
          {detail.data && (
            <PageSummary
              problems={problems}
              page={page}
              delegatedElements={detail.data.global.delegatedElements}
            />
          )}
          <div className="inspector-layout" ref={layout}>
            <section className="inspector-issues" aria-labelledby="pagina-incidencias">
              <h3 id="pagina-incidencias" className="sr-only">
                {t('scan.pages.issues', { count: issues.length })}
              </h3>
              {detail.isPending ? (
                <p role="status">{t('app.loading')}</p>
              ) : detail.isError ? (
                <Message tone="error">{t('errors.generic')}</Message>
              ) : (
                <ResultsTabs
                  scope="page"
                  issues={issues}
                  checks={detail.data.checks}
                  manual={null}
                  pages={[]}
                  scanId={scan.id}
                  siteId={siteId}
                  rights={rights}
                  tab={tab}
                  onTab={setTab}
                  reveal={null}
                  persistKey={`${scan.id}:${page.key}`}
                  pageContext={{
                    scan,
                    page,
                    device,
                    selectedId: elementId,
                    onSelect: select,
                    heights,
                    problemKey: openKey,
                    onOpenProblem: openProblem,
                    stacked,
                    onShowCapture: () => setCaptureShown(true),
                  }}
                />
              )}
            </section>
            {!stacked ? (
              inspector
            ) : !panelOpen ? (
              <details
                className="capture-fold"
                open={captureOpen}
                onToggle={(event) => setCaptureOpen(event.currentTarget.open)}
              >
                <summary>{t('inspector.title')}</summary>
                {inspector}
              </details>
            ) : (
              <ModalOverlay
                isOpen={captureShown}
                onOpenChange={setCaptureShown}
                isDismissable
                className="capture-overlay"
              >
                <Modal className="capture-modal">
                  <Dialog aria-label={t('element.panel.captureTitle')} className="outline-none">
                    <div className="capture-modal-head">
                      <Button variant="secondary" onPress={() => setCaptureShown(false)} autoFocus>
                        <ArrowLeftIcon aria-hidden="true" />
                        {t('element.panel.backToSolution')}
                      </Button>
                    </div>
                    {selection && (
                      <ElementNavigator
                        elements={selection.elements}
                        selectedId={selection.element.id}
                        total={selection.problem.elements}
                        onSelect={(id) => select(id)}
                      />
                    )}
                    {inspector}
                  </Dialog>
                </Modal>
              </ModalOverlay>
            )}
          </div>
        </>
      )}
      <TechnicalDetails scan={scan} siteId={siteId} page={page} />
    </article>
  );
}

/** The measurements and the context of the analysis, folded away: they are not needed to fix a problem. */
function TechnicalDetails({ scan, siteId, page }: { scan: ScanDetail; siteId: string; page: ScannedPage }) {
  const { t, i18n } = useTranslation();
  return (
    <details className="page-tech">
      <summary>
        <span>{t('scan.technical')}</span>
        <ChevronDownIcon className="page-tech-arrow" aria-hidden="true" />
      </summary>
      <div className="page-tech-body">
        <Facts page={page} />
        <ResultContext scan={scan} siteId={siteId} />
        <CountingTerms />
        <p>
          <WebsiteLink href={publicPage(i18n.language, 'methodology')}>
            {t('results.honesty.link')}
          </WebsiteLink>
        </p>
      </div>
    </details>
  );
}
